import { ChangeDetectionStrategy, Component, Input, OnChanges, OnInit, SimpleChanges, inject } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { FinishedPlantListResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/location/finished-plant-list.response';
import { SharedModule } from '../../../@commons/ui-components/shared.module';
import { UndoSnackbarComponent } from '../../../@commons/ui-components/undo-snackbar/undo-snackbar.component';
import { UndoableList } from '../../../@commons/ui-components/undo-snackbar/undoable-list';
import { LocationLabelPipe } from '../../../@commons/pipes/location-label.pipe';
import { VwPluralPipe } from '../../../@commons/pipes/vw-plural.pipe';
import { FinishedPlantsStore } from '../../../@commons/@services/data/finished-plants.store';
import { StorageService } from '../../../@commons/@services/front/storage.service';
import { FinishedReasonText, finishedReasonText } from './finished-plant-reason';

/**
 * « 5 cultures semblent finies, on les range ? » : une carte discrète sous la
 * carte du jour, seulement quand le jardin affiché en a, et la feuille qui les
 * liste avec leur raison en clair.
 *
 * Proposer, jamais ranger d'office (décision 2) : « Ranger » une plante ou
 * « Tout ranger », avec « Annuler » ; le rangement ne part qu'à la fermeture
 * du message (`UndoableList`). « Elle pousse encore » ouvre sa fiche, pour
 * recaler son étape, et ne range rien.
 */
@Component({
  selector: 'vw-finished-plants',
  standalone: true,
  imports: [NgTemplateOutlet, SharedModule, TranslatePipe, VwPluralPipe, LocationLabelPipe, UndoSnackbarComponent],
  templateUrl: './finished-plants.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class FinishedPlantsComponent extends UndoableList implements OnInit, OnChanges {
  /**
   * Chaque nouvelle valeur demande d'ouvrir la liste (`/home?finished=1`, push
   * de saison) : relue, elle s'ouvre s'il y a quelque chose à ranger.
   */
  @Input() openRequest = 0;

  plants: FinishedPlantListResponse[] = [];
  sheetOpen = false;
  private reasons = new Map<string, FinishedReasonText>();
  /** Une demande d'ouverture attend la fin de la lecture. */
  private openRequested = false;
  /** « Tout ranger » deux fois de suite : deux messages, deux clés. */
  private tidyAllCount = 0;

  private readonly store = inject(FinishedPlantsStore);
  private readonly storage = inject(StorageService);
  private readonly router = inject(Router);

  async ngOnInit() {
    await this.load();
  }

  ngOnChanges(changes: SimpleChanges) {
    const request = changes['openRequest'];
    if (!request || !this.openRequest) return;
    this.openRequested = true;
    // Déjà affichée : la liste est relue, le push vient peut-être d'en compter de nouvelles.
    if (!request.firstChange) void this.load();
  }

  /** Best-effort : une panne ne retire que la carte. */
  async load() {
    try {
      this.plants = await this.store.list();
    } catch (e) {
      console.warn('FinishedPlantsComponent: failed to load the finished plants', e);
      this.plants = [];
    }
    const lang = this.storage.getCurrentLanguage();
    this.reasons = new Map(this.plants.map((plant) => [plant.uid, finishedReasonText(plant, lang)]));
    if (this.openRequested) {
      this.openRequested = false;
      this.openSheet();
    }
  }

  /** Les cultures encore proposées : ni rangées, ni en attente du message. */
  get shown(): FinishedPlantListResponse[] {
    return this.plants.filter((plant) => !this.gone.has(plant.uid) && !this.checked.has(plant.uid));
  }

  reasonOf(plant: FinishedPlantListResponse): FinishedReasonText {
    return this.reasons.get(plant.uid)!;
  }

  /** La feuille, s'il reste quelque chose à y montrer. */
  openSheet() {
    if (this.shown.length) this.sheetOpen = true;
  }

  tidy(plant: FinishedPlantListResponse) {
    this.tidyRows([plant], plant.uid);
  }

  tidyAll() {
    this.tidyRows(this.shown, `all-${++this.tidyAllCount}`);
  }

  /** « Elle pousse encore » : sa fiche, ouverte sur le choix de son étape. Rien n'est rangé. */
  stillGrowing(plant: FinishedPlantListResponse) {
    this.sheetOpen = false;
    void this.router.navigate(['/garden/plants', plant.uid], { queryParams: { action: 'choose-step' } });
  }

  /**
   * Les lignes quittent la liste tout de suite, le rangement attend la
   * fermeture du message. Une plante que le serveur n'a pas rangée revient.
   * La liste vidée, la feuille se ferme ; « Annuler » ramène la carte.
   */
  private tidyRows(plants: FinishedPlantListResponse[], key: string) {
    if (!plants.length) return;
    this.settle();
    plants.forEach((plant) => this.check(plant.uid));
    this.pendingUndo = {
      key,
      rows: plants.map((plant) => plant.uid),
      message: 'home.finished.undo',
      count: plants.length,
      commit: async () => {
        const result = await this.store.tidy(plants);
        result.failedUids.forEach((uid) => this.restore(uid));
      },
    };
    if (!this.shown.length) this.sheetOpen = false;
  }
}
